import { Box, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import { useTranslation } from 'react-i18next';
import {
  useInsights,
  isActiveInsight,
  useMutedInsightTypes,
  useUnmuteInsightType,
} from '@/hooks/useInsights';
import { InsightCard } from './InsightCard';

/**
 * A muted type's display label. Insight types are dotted (`booking.confirmed.past_schedule`)
 * while their i18n keys are camelCase (`insights.bookingConfirmedPastSchedule.title`) — the
 * same mapping the detection rules use. Falls back to the raw type when a type has
 * no translation yet, so a new insight type never renders as a blank chip.
 */
function insightTypeTitleKey(insightType: string): string {
  const camel = insightType.replace(/[._]+(.)/gu, (_, c: string) => c.toUpperCase());
  return `insights.${camel}.title`;
}

/**
 * Muted insight types with a one-click way back (issue #1214). Rendered under the
 * list — and in the empty state, where it explains why the inbox looks quiet.
 */
function MutedTypes() {
  const { t } = useTranslation('assistant');
  const { data } = useMutedInsightTypes();
  const unmute = useUnmuteInsightType();
  const muted = data?.mutedTypes ?? [];

  if (muted.length === 0) return null;

  return (
    <Box sx={{ pt: 1 }}>
      <Divider sx={{ mb: 1 }} />
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
        <NotificationsOffOutlinedIcon fontSize="small" color="disabled" />
        <Typography variant="caption" color="text.secondary">
          {t('inbox.muted.title')}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {muted.map((m) => (
          <Chip
            key={m.insightType}
            size="small"
            variant="outlined"
            label={t(insightTypeTitleKey(m.insightType), { defaultValue: m.insightType })}
            onDelete={() => unmute.mutate(m.insightType)}
            disabled={unmute.isPending}
          />
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {t('inbox.muted.hint')}
      </Typography>
    </Box>
  );
}

/**
 * Proactive OS Buddy inbox (issue #1090). Server-backed list of active insights;
 * shares the cached `useInsights` query with the panel's tab-count badge. It never
 * writes to the chat history — opening an insight is an explicit user action.
 */
export function InsightsInbox() {
  const { t } = useTranslation('assistant');
  const { data, isLoading, isError, refetch } = useInsights();

  const active = (data?.insights ?? []).filter((i) => isActiveInsight(i));

  if (isLoading) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('inbox.error')}
        </Typography>
        <Typography
          variant="body2"
          color="primary"
          role="button"
          tabIndex={0}
          onClick={() => void refetch()}
          onKeyDown={(e) => { if (e.key === 'Enter') void refetch(); }}
          sx={{ cursor: 'pointer' }}
        >
          {t('inbox.retry')}
        </Typography>
      </Box>
    );
  }

  if (active.length === 0) {
    return (
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4, textAlign: 'center' }}>
          <InboxOutlinedIcon color="disabled" sx={{ fontSize: 40 }} />
          <Typography variant="body2" color="text.secondary">
            {t('inbox.empty')}
          </Typography>
        </Box>
        <MutedTypes />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5 }}>
      <Stack spacing={1.5}>
        {active.map((insight) => (
          <InsightCard key={insight.id} insight={insight} />
        ))}
      </Stack>
      <MutedTypes />
    </Box>
  );
}
